import React, { useState, useEffect, useRef, forwardRef, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  InputNumber,
  Checkbox,
  App,
  Upload,
  Radio
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  EyeInvisibleOutlined,
  DragOutlined,
  SaveOutlined,
  CrownOutlined,
  FileTextOutlined,
  FontSizeOutlined,
  PictureOutlined,
  MessageOutlined,
  ContactsOutlined,
  AppstoreOutlined,
  DollarOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
  FolderOpenOutlined,
  CopyOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import 'react-quill/dist/quill.snow.css';
import type ReactQuill from 'react-quill';
import { websiteApi } from '../services/websiteApi';
import { videoApi } from '../services/videoApi';
import { websiteGeneratorService } from '../services/websiteGeneratorApi';
import { progressApi } from '../services/api';
import DndKitMenuDragDrop from '../components/DndKitMenuDragDrop';
import ProgressModal from '../components/ProgressModal';
import SidebarBuilder from '../components/SidebarBuilder';
import TopKeywordsBuilder from '../components/TopKeywordsBuilder';
import { MenuItem, SidebarConfig, TopKeywordsConfig } from '../types/website';

interface BannerAd {
  id: string;
  position: 'top' | 'left' | 'right' | 'bottom';
  imageUrl: string;
  linkUrl: string;
}

interface FooterLink {
  id: string;
  label: string;
  url: string;
  dofollow?: boolean;
}

const normalizeFooterLinks = (
  links: Array<Partial<FooterLink> & { label: string; url: string }>,
): FooterLink[] => {
  const seen = new Set<string>();

  return links.map((link, index) => {
    let id = String(link.id ?? '').trim();
    if (!id || seen.has(id)) {
      id = `footer_link_${index}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    }
    seen.add(id);

    return {
      id,
      label: link.label,
      url: link.url,
      dofollow: link.dofollow ?? false,
    };
  });
};

const cloneFooterLinks = (links: FooterLink[]): FooterLink[] =>
  links.map((link) => ({ ...link }));

const { TextArea } = Input;
const { Option } = Select;

// Wrapper component removed - using lazy loading instead

// Lazy load ReactQuill to avoid findDOMNode warning
const LazyQuillEditor = lazy(() => 
  import('react-quill').then(module => ({
    default: forwardRef<ReactQuill, any>((props, ref) => {
      return <module.default ref={ref} {...props} />;
    })
  }))
);

// Rich Text Editor component with lazy loading and warning suppression
const RichTextEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
  const quillRef = useRef<ReactQuill>(null);
  
  // Suppress findDOMNode warning
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('findDOMNode')) {
        return;
      }
      originalError.apply(console, args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);
  
  return (
    <Suspense fallback={<div style={{ height: '200px', border: '1px solid #d9d9d9', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading editor...</div>}>
      <LazyQuillEditor
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        style={{ height: '200px', marginBottom: '50px' }}
        modules={{
          toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean']
          ],
        }}
      />
    </Suspense>
  );
};

interface WebsiteSection {
  id: string;
  type: 'hero' | 'content' | 'gallery' | 'testimonials' | 'contact' | 'features' | 'pricing' | 'video' | 'text' | 'video-grid';
  title?: string;
  subtitle?: string;
  content?: string;
  richContent?: string; // For rich text editor content (hero, text, content sections)
  imageUrl?: string;
  imageUrls?: string[]; // For gallery sections - multiple images
  videoUrl?: string;
  videoUrls?: string[]; // For video-grid sections - multiple video URLs (manual mode)
  selectedVideoIds?: string[]; // For video-grid sections - selected video IDs from database
  videoSelectionMode?: 'manual' | 'all' | 'random' | 'random-range'; // For video-grid sections - manual selection, all videos, random selection, or random range
  randomVideoCount?: number; // For video-grid sections - number of random videos to pick
  randomVideoCountMin?: number; // For video-grid sections - minimum number for random range
  randomVideoCountMax?: number; // For video-grid sections - maximum number for random range
  postsPerPage?: number; // For video-grid sections - number of videos per page
  enablePagination?: boolean; // For video-grid sections - enable pagination
  gridColumns?: number; // For video-grid sections - number of columns (2-5)
  cardStyle?: string; // For video-grid sections - card style (glass, elevated, outlined, flat)
  animation?: string; // For video-grid sections - animation type (dynamic, subtle, none)
  showVideoInfo?: boolean; // For video-grid sections - show titles, durations, etc.
  backgroundColor?: string;
  textColor?: string;
  padding?: string;
  margin?: string;
  order: number;
  visible: boolean;
  fullWidth?: boolean;
  settings?: {
    [key: string]: any;
  };
}

interface Video {
  id: string;
  title: string;
  videoLink: string;
  imageUrl?: string;
  duration?: number;
  viewCount: number;
}

const sectionTypes = [
  { value: 'hero', label: 'Hero Section', icon: <CrownOutlined style={{ color: '#FFD700' }} /> },
  { value: 'content', label: 'Content Block', icon: <FileTextOutlined style={{ color: '#1890FF' }} /> },
  { value: 'text', label: 'Text Section', icon: <FontSizeOutlined style={{ color: '#52C41A' }} /> },
  { value: 'gallery', label: 'Image Gallery', icon: <PictureOutlined style={{ color: '#FF7A45' }} /> },
  { value: 'testimonials', label: 'Testimonials', icon: <MessageOutlined style={{ color: '#722ED1' }} /> },
  { value: 'contact', label: 'Contact Form', icon: <ContactsOutlined style={{ color: '#13C2C2' }} /> },
  { value: 'features', label: 'Features', icon: <AppstoreOutlined style={{ color: '#EB2F96' }} /> },
  { value: 'pricing', label: 'Pricing', icon: <DollarOutlined style={{ color: '#FA8C16' }} /> },
  { value: 'video', label: 'Video', icon: <PlayCircleOutlined style={{ color: '#F5222D' }} /> },
  { value: 'video-grid', label: 'Video Grid', icon: <VideoCameraOutlined style={{ color: '#2F54EB' }} /> }
];

const WebsiteBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [footerLinkForm] = Form.useForm();
  const { message } = App.useApp();

  const [sections, setSections] = useState<WebsiteSection[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSection, setEditingSection] = useState<WebsiteSection | null>(null);
  const [sectionType, setSectionType] = useState<string>('content');
  const [loading, setLoading] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [textColor, setTextColor] = useState('#000000');
  const [availableVideos, setAvailableVideos] = useState<Video[]>([]);
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [videoSelectionMode, setVideoSelectionMode] = useState<'manual' | 'all' | 'random' | 'random-range'>('manual');
  const [randomVideoCount, setRandomVideoCount] = useState<number>(5);
  const [randomVideoCountMin, setRandomVideoCountMin] = useState<number>(1000);
  const [randomVideoCountMax, setRandomVideoCountMax] = useState<number>(1500);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [websiteData, setWebsiteData] = useState<any>(null);
  const [websiteSectionsData, setWebsiteSectionsData] = useState<any>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<string | null>(null);
  const [bannerAds, setBannerAds] = useState<BannerAd[]>([]);
  const [bannerModalVisible, setBannerModalVisible] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerAd | null>(null);
  
  // Footer management states
  const [footerStyle, setFooterStyle] = useState<string>('classic-3col');
  const [footerText, setFooterText] = useState<string>('');
  const [footerLinks, setFooterLinks] = useState<FooterLink[]>([]);
  const [footerCompanyDescription, setFooterCompanyDescription] = useState<string>('');
  const companyDescriptionDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [footerModalVisible, setFooterModalVisible] = useState(false);
  const [editingFooterLink, setEditingFooterLink] = useState<FooterLink | null>(null);
  const [editingFooterLinkIndex, setEditingFooterLinkIndex] = useState<number | null>(null);
  const [isImportFooterLinksModalVisible, setIsImportFooterLinksModalVisible] = useState(false);
  const [importFooterLinksMode, setImportFooterLinksMode] = useState<'insert_and_update_duplicate' | 'replace_all'>('insert_and_update_duplicate');
  
  // Footer style options
  const footerStyleOptions = [
    { value: 'classic-3col', label: 'Classic 3-Column', description: 'Company info, links, and subscribe form' },
    { value: 'minimal-2col', label: 'Minimal 2-Column', description: 'Simple two-column layout' },
    { value: 'centered', label: 'Centered', description: 'Centered single column layout' },
    { value: 'split', label: 'Split Footer', description: '4 columns top section with bottom bar' },
    { value: 'simple-horizontal', label: 'Simple Horizontal', description: 'Horizontal layout with logo and links' },
    { value: 'magazine', label: 'Magazine Style', description: '4 columns with newsletter section' },
    { value: 'ecommerce', label: 'E-commerce', description: 'Customer service focused with payment methods' },
    { value: 'modern-minimal', label: 'Modern Minimal', description: 'Clean minimal design' },
  ];
  
  // Reset footer link form when editingFooterLink changes
  useEffect(() => {
    if (editingFooterLink) {
      footerLinkForm.setFieldsValue({
        label: editingFooterLink.label,
        url: editingFooterLink.url,
        dofollow: editingFooterLink.dofollow || false
      });
    } else {
      footerLinkForm.resetFields();
    }
  }, [editingFooterLink, footerLinkForm]);
  
  // Footer pagination states
  const [footerCurrentPage, setFooterCurrentPage] = useState<number>(1);
  const [footerPageSize] = useState<number>(10);
  
  // Footer template states
  const [footerTemplates, setFooterTemplates] = useState<Array<{
    id: string;
    name: string;
    text: string;
    links: Array<{id: string, label: string, url: string, dofollow?: boolean}>;
    createdAt: string;
  }>>([]);
  const [footerTemplateModalVisible, setFooterTemplateModalVisible] = useState(false);
  const [footerTemplateName, setFooterTemplateName] = useState<string>('');

  // Progress modal state
  const [progressVisible, setProgressVisible] = useState(false);
  const [progressData, setProgressData] = useState({
    title: '',
    overallProgress: 0,
    items: [] as Array<{
      id: string;
      name: string;
      status: 'pending' | 'processing' | 'completed' | 'error';
      progress?: number;
    }>,
    isCompleted: false,
    isError: false,
    successMessage: '',
    errorMessage: '',
  });

  // Generation selection state
  const [generationSelection, setGenerationSelection] = useState({
    menuStructure: false,
    bannerAds: false,
    footerManagement: false,
    sidebar: false,
    topKeywords: false,
    googleAnalytics: false,
    websiteSections: {
      enabled: false,
      subSections: [] as Array<{
        id: string;
        name: string;
        selected: boolean;
        type: string;
      }>
    }
  });
  
  // SSL status state
  const [hasSSL, setHasSSL] = useState(false);
  const [sslLoading, setSslLoading] = useState(false);
  
  // Sidebar state
  const [sidebarConfig, setSidebarConfig] = useState<SidebarConfig>({
    enabled: false,
    position: 'right',
    width: '300px',
    customWidth: undefined,
  });

  const [topKeywordsConfig, setTopKeywordsConfig] = useState<TopKeywordsConfig>({
    enabled: false,
    title: 'Top từ khóa:',
    keywords: [],
  });

  const [gaKey, setGaKey] = useState<string>('');
  const [gaKeySaving, setGaKeySaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (id) {
      // Load website sections data first (contains both sections and config)
      loadWebsiteSectionsData().then(async () => {
        // Then load sections and menu items from cached data
        loadWebsiteSections();
        loadMenuItems();
        await loadBannerAds();
        await loadFooterData();
        await loadSidebarData();
        await loadTopKeywordsData();
        await loadGaKeyData();
      });
      
      // Load other data in parallel
      loadWebsiteData();
      loadAvailableVideos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Check SSL status when website data is loaded
  useEffect(() => {
    if (websiteData?.domain) {
      checkSSLStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteData?.domain]);

  // Load footer templates on component mount
  useEffect(() => {
    loadFooterTemplates();
  }, []);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (companyDescriptionDebounceRef.current) {
        clearTimeout(companyDescriptionDebounceRef.current);
      }
    };
  }, []);

  // Sync sub-sections with current sections
  useEffect(() => {
    const subSections = sections.map((section, index) => ({
      id: section.id,
      name: `${section.type.charAt(0).toUpperCase() + section.type.slice(1)} Section ${index + 1}`,
      selected: false,
      type: section.type
    }));
    
    setGenerationSelection(prev => ({
      ...prev,
      websiteSections: {
        ...prev.websiteSections,
        subSections
      }
    }));
  }, [sections]);

  const loadWebsiteData = async () => {
    try {
      const response = await websiteApi.getById(id!);
      setWebsiteData(response);
    } catch (error) {
      console.error('Failed to load website data:', error);
    }
  };

  const checkSSLStatus = async () => {
    if (!websiteData?.domain) return;
    
    setSslLoading(true);
    try {
      const response = await fetch(`/api/nginx/sites/${websiteData.domain}/status`);
      const data = await response.json();
      
      if (data.configured) {
        // Check if SSL certificate exists
        const sslResponse = await fetch(`/api/nginx/ssl-status/${websiteData.domain}`);
        const sslData = await sslResponse.json();
        setHasSSL(sslData.hasSSL || false);
      } else {
        setHasSSL(false);
      }
    } catch (error) {
      console.error('Error checking SSL status:', error);
      setHasSSL(false);
    } finally {
      setSslLoading(false);
    }
  };

  const loadWebsiteSectionsData = async () => {
    try {
      const response = await websiteApi.getWebsiteSections(id!);
      setWebsiteSectionsData(response);
      return response;
    } catch (error) {
      console.error('Failed to load website sections data:', error);
      message.error('Failed to load website sections data');
      return null;
    }
  };

  const loadWebsiteSections = async () => {
    try {
      const response = await loadWebsiteSectionsData();
      setSections(response?.sections || []);
    } catch (error) {
      console.error('Failed to load website sections:', error);
      message.error('Failed to load website sections');
    }
  };

  const loadAvailableVideos = async () => {
    try {
      const videos = await videoApi.getAll();
      setAvailableVideos(videos);
    } catch (error) {
      console.error('Failed to load videos:', error);
    }
  };

  const loadMenuItems = async () => {
    try {
      // Use cached data if available, otherwise load fresh data
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      // Check if response has config data with menu (only use web_builder.menu)
      let menuData = null;
      if (response && response.config && response.config.web_builder) {
        // Only use web_builder.menu
        if (response.config.web_builder.menu && Array.isArray(response.config.web_builder.menu)) {
          menuData = response.config.web_builder.menu;
        }
      }
      
      if (menuData) {
        setMenuItems(menuData);
      } else {
        // Default menu items
        setMenuItems([
          {
            id: 'home',
            label: 'Trang chủ',
            url: '/'
          },
          {
            id: 'sex-vietnam',
            label: 'Sex Việt Nam',
            url: '/sex-vietnam'
          },
          {
            id: 'sex-my-chau-au',
            label: 'Sex Mỹ, Châu Âu',
            url: '/sex-my-chau-au'
          },
          {
            id: 'sex-hoc-sinh-jav-hd',
            label: 'Sex Học Sinh JAV HD',
            url: '/sex-hoc-sinh-jav-hd'
          },
          {
            id: 'trung-quoc',
            label: 'Trung Quốc',
            url: '/trung-quoc'
          },
          {
            id: 'phim-sex-vietsub',
            label: 'Phim Sex Vietsub',
            url: '/phim-sex-vietsub'
          },
          {
            id: 'phim-sex-khong-che',
            label: 'Phim Sex Không Che',
            url: '/phim-sex-khong-che'
          },
          {
            id: 'phim-sex-gai-xinh',
            label: 'Phim Sex Gái Xinh',
            url: '/phim-sex-gai-xinh'
          },
          {
            id: 'vung-trom-ngoai-tinh',
            label: 'Vụng Trộm - Ngoại Tình',
            url: '/vung-trom-ngoai-tinh'
          },
          {
            id: 'phim-cap-3',
            label: 'Phim cấp 3',
            url: '/phim-cap-3'
          }
        ]);
      }
    } catch (error) {
      console.error('Failed to load menu items:', error);
      message.error('Failed to load menu items');
    }
  };

  const handleMenuItemsChange = React.useCallback((newMenuItems: MenuItem[]) => {
    setMenuItems(newMenuItems);
  }, []);

  const shuffleMenuItems = () => {
    // Fisher-Yates shuffle algorithm
    const shuffled = [...menuItems];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setMenuItems(shuffled);
    message.success('Menu items shuffled successfully!');
  };

  const saveMenuItems = async () => {
    try {
      // Use cached data if available, otherwise load fresh data
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      let config = response?.config || {};
      
      // Initialize web_builder if it doesn't exist
      if (!config.web_builder) {
        config.web_builder = {};
      }
      
      // Only save menu to web_builder.menu
      config.web_builder.menu = menuItems;
      
      // Save config via new updateConfig API
      await websiteApi.updateConfig(id!, config);
      
      // Update cached data
      setWebsiteSectionsData({ ...response, config });
      
      message.success('Menu structure saved to config.json!');
    } catch (error) {
      console.error('Failed to save menu to config.json:', error);
      message.error('Failed to save menu structure');
    }
  };

  const loadBannerAds = async () => {
    try {
      // Use cached data if available, otherwise load fresh data
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      const config = response?.config || {};
      const bannerAdsData = config?.web_builder?.bannerAds || [];
      
      setBannerAds(bannerAdsData);
    } catch (error) {
      console.error('Failed to load banner ads:', error);
    }
  };

  const saveBannerAds = async (bannerAdsToSave?: BannerAd[]) => {
    try {
      // Use cached data if available, otherwise load fresh data
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      let config = response?.config || {};
      
      // Initialize web_builder if it doesn't exist
      if (!config.web_builder) {
        config.web_builder = {};
      }
      
      // Save banner ads to web_builder.bannerAds
      const adsToSave = bannerAdsToSave || bannerAds;
      config.web_builder.bannerAds = adsToSave;
      
      // Save config via new updateConfig API
      await websiteApi.updateConfig(id!, config);
      
      // Update cached data
      setWebsiteSectionsData({ ...response, config });
      
      message.success('Banner ads saved to config.json!');
    } catch (error) {
      console.error('Failed to save banner ads to config.json:', error);
      message.error('Failed to save banner ads');
    }
  };

  // Banner Ads handlers
  const handleAddBanner = () => {
    setEditingBanner(null);
    setBannerModalVisible(true);
  };

  const handleEditBanner = (banner: BannerAd) => {
    setEditingBanner(banner);
    setBannerModalVisible(true);
  };

  const handleDeleteBanner = async (bannerId: string) => {
    const newBannerAds = bannerAds.filter(banner => banner.id !== bannerId);
    setBannerAds(newBannerAds);
    message.success('Banner ad deleted successfully');
    
    // Save banner ads to config with the new data
    await saveBannerAds(newBannerAds);
  };

  const handleSaveBanner = async (values: any) => {
    try {
      const bannerData: BannerAd = {
        id: editingBanner?.id || `banner-${Date.now()}`,
        position: values.position,
        imageUrl: values.imageUrl,
        linkUrl: values.linkUrl
      };

      let newBannerAds: BannerAd[];
      
      if (editingBanner) {
        // Update existing banner
        newBannerAds = bannerAds.map(banner => 
          banner.id === editingBanner.id ? bannerData : banner
        );
        setBannerAds(newBannerAds);
        message.success('Banner ad updated successfully');
      } else {
        // Add new banner
        if (bannerAds.length >= 10) {
          message.error('Maximum 10 banner ads allowed');
          return;
        }
        newBannerAds = [...bannerAds, bannerData];
        setBannerAds(newBannerAds);
        message.success('Banner ad added successfully');
      }

      setBannerModalVisible(false);
      setEditingBanner(null);
      
      // Save banner ads to config with the new data
      await saveBannerAds(newBannerAds);
    } catch (error) {
      console.error('Failed to save banner ad:', error);
      message.error('Failed to save banner ad');
    }
  };

  // Footer management functions
  const loadFooterData = async () => {
    try {
      // Use cached data if available, otherwise load fresh data
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      const config = response?.config || {};
      const footerData = config?.web_builder?.footer || {};
      
      setFooterStyle(footerData.style || 'classic-3col');
      setFooterText(footerData.text || '');
      setFooterLinks(normalizeFooterLinks(footerData.links || []));
      setFooterCompanyDescription(footerData.companyDescription || '');
      
    } catch (error) {
      console.error('Failed to load footer data:', error);
    }
  };

  const loadSidebarData = async () => {
    try {
      // Use cached data if available, otherwise load fresh data
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      const config = response?.config || {};
      const sidebarData = config?.web_builder?.sidebar || {};
      
      setSidebarConfig({
        enabled: sidebarData.enabled || false,
        position: sidebarData.position || 'right',
        width: sidebarData.width || '300px',
        customWidth: sidebarData.customWidth,
        title: sidebarData.title || '',
        videoSelectionMode: sidebarData.videoSelectionMode || 'random',
        selectedVideoIds: sidebarData.selectedVideoIds || [],
        rankFrom: sidebarData.rankFrom || 1,
        rankTo: sidebarData.rankTo || 6,
      });
      
    } catch (error) {
      console.error('Failed to load sidebar data:', error);
    }
  };

  const saveSidebarData = async (sidebarConfigToSave?: SidebarConfig) => {
    try {
      // Use cached data if available, otherwise load fresh data
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      let config = response?.config || {};
      
      // Initialize web_builder if it doesn't exist
      if (!config.web_builder) {
        config.web_builder = {};
      }
      
      // Save sidebar config to web_builder.sidebar
      const configToSave = sidebarConfigToSave || sidebarConfig;
      config.web_builder.sidebar = configToSave;
      
      // Save config via new updateConfig API
      await websiteApi.updateConfig(id!, config);
      
      // Update cached data
      setWebsiteSectionsData({ ...response, config });
      
      // Update local state
      setSidebarConfig(configToSave);
      
      message.success('Sidebar configuration saved!');
    } catch (error) {
      console.error('Failed to save sidebar data:', error);
      message.error('Failed to save sidebar configuration');
    }
  };

  const loadTopKeywordsData = async () => {
    try {
      // Use cached data if available
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      const config = response?.config;
      // Check new location first (web_builder.topKeywords), fallback to old location (sidebar.topKeywords)
      let topKeywordsData = config?.web_builder?.topKeywords;
      
      // Migrate from old sidebar location if needed
      if (!topKeywordsData && config?.web_builder?.sidebar?.topKeywords) {
        const sidebarData = config.web_builder.sidebar;
        topKeywordsData = {
          enabled: sidebarData.topKeywordsEnabled || false,
          title: sidebarData.topKeywordsTitle || 'Top từ khóa:',
          keywords: sidebarData.topKeywords || [],
        };
      }
      
      topKeywordsData = topKeywordsData || {};
      
      setTopKeywordsConfig({
        enabled: topKeywordsData.enabled || false,
        title: topKeywordsData.title || 'Top từ khóa:',
        keywords: topKeywordsData.keywords || [],
      });
    } catch (error) {
      console.error('Failed to load top keywords data:', error);
    }
  };

  const saveTopKeywordsData = async (topKeywordsConfigToSave?: TopKeywordsConfig) => {
    try {
      // Use cached data if available, otherwise load fresh data
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      let config = response?.config || {};
      
      // Initialize web_builder if it doesn't exist
      if (!config.web_builder) {
        config.web_builder = {};
      }
      
      // Save top keywords config to web_builder.topKeywords
      const configToSave = topKeywordsConfigToSave || topKeywordsConfig;
      config.web_builder.topKeywords = configToSave;
      
      // Save config via updateConfig API
      await websiteApi.updateConfig(id!, config);
      
      // Update cached data
      setWebsiteSectionsData({ ...response, config });
      
      // Update local state
      setTopKeywordsConfig(configToSave);
      
      message.success('Top keywords configuration saved!');
    } catch (error) {
      console.error('Failed to save top keywords data:', error);
      message.error('Failed to save top keywords configuration');
    }
  };

  const loadGaKeyData = async () => {
    try {
      const response = await websiteApi.getById(id!);
      setGaKey(response?.ga_tag_key || '');
    } catch (error) {
      console.error('Failed to load GA tag key:', error);
    }
  };

  const saveGaKeyData = async (gaTagKeyToSave?: string) => {
    try {
      setGaKeySaving(true);

      const value = (gaTagKeyToSave !== undefined ? gaTagKeyToSave : gaKey).trim();
      await websiteApi.update(id!, { ga_tag_key: value });

      setGaKey(value);
      setWebsiteData((prev: any) => (prev ? { ...prev, ga_tag_key: value || undefined } : prev));

      message.success('GA Tag Key saved!');
    } catch (error) {
      console.error('Failed to save GA tag key:', error);
      message.error('Failed to save GA Tag Key');
    } finally {
      setGaKeySaving(false);
    }
  };

  const saveFooterData = async (
    footerTextToSave?: string, 
    footerLinksToSave?: FooterLink[],
    footerStyleToSave?: string,
    footerCompanyDescriptionToSave?: string
  ) => {
    try {
      // Use cached data if available, otherwise load fresh data
      let response = websiteSectionsData;
      if (!response) {
        response = await loadWebsiteSectionsData();
      }
      
      let config = response?.config || {};
      
      // Initialize web_builder if it doesn't exist
      if (!config.web_builder) {
        config.web_builder = {};
      }
      
      // Save footer data to web_builder.footer
      const textToSave = footerTextToSave !== undefined ? footerTextToSave : footerText;
      const linksToSave = normalizeFooterLinks(
        footerLinksToSave !== undefined ? footerLinksToSave : footerLinks,
      );
      const styleToSave = footerStyleToSave !== undefined ? footerStyleToSave : footerStyle;
      const companyDescriptionToSave = footerCompanyDescriptionToSave !== undefined ? footerCompanyDescriptionToSave : footerCompanyDescription;
      
      config.web_builder.footer = {
        style: styleToSave,
        text: textToSave,
        links: cloneFooterLinks(linksToSave),
        companyDescription: companyDescriptionToSave
      };
      
      // Save config via new updateConfig API
      await websiteApi.updateConfig(id!, config);
      
      // Update cached data (important: this ensures reload shows the new value)
      setWebsiteSectionsData({ ...response, config });
      
      // Update local state to match saved data
      if (footerStyleToSave !== undefined) {
        setFooterStyle(footerStyleToSave);
      }
      if (footerTextToSave !== undefined) {
        setFooterText(footerTextToSave);
      }
      if (footerLinksToSave !== undefined) {
        setFooterLinks(footerLinksToSave);
      }
      if (footerCompanyDescriptionToSave !== undefined) {
        setFooterCompanyDescription(footerCompanyDescriptionToSave);
      }
      
      message.success('Footer data saved to config.json!');
    } catch (error) {
      console.error('Failed to save footer data to config.json:', error);
      message.error('Failed to save footer data');
    }
  };

  // Footer handlers
  const handleAddFooterLink = () => {
    setEditingFooterLink(null);
    setEditingFooterLinkIndex(null);
    footerLinkForm.resetFields();
    setFooterModalVisible(true);
  };

  const handleEditFooterLink = (link: FooterLink, pageLocalIndex: number) => {
    const globalIndex = (footerCurrentPage - 1) * footerPageSize + pageLocalIndex;
    setEditingFooterLink({ ...link });
    setEditingFooterLinkIndex(globalIndex);
    setFooterModalVisible(true);
  };

  const handleUploadFooterLinks = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          message.error('CSV file must have at least a header row and one data row');
          return;
        }

        // Parse CSV (assuming format: text,link)
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const textIndex = headers.findIndex(h => h === 'text' || h === 'label');
        const linkIndex = headers.findIndex(h => h === 'link' || h === 'url');

        if (textIndex === -1 || linkIndex === -1) {
          message.error('CSV must have "text" and "link" columns');
          return;
        }

        const newLinks: FooterLink[] = [];
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          // Handle CSV with quoted values
          const values = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g)?.map(v => v.trim().replace(/^"|"$/g, '')) || line.split(',');
          
          if (values.length > Math.max(textIndex, linkIndex)) {
            const label = values[textIndex]?.trim();
            const url = values[linkIndex]?.trim();

            if (label && url) {
              newLinks.push({
                id: `link_${Date.now()}_${i}`,
                label: label,
                url: url,
                dofollow: false // All uploaded links are nofollow
              });
            }
          }
        }

        if (newLinks.length === 0) {
          message.error('No valid links found in CSV file');
          return;
        }

        let updatedLinks: FooterLink[];
        let messageText = '';

        if (importFooterLinksMode === 'replace_all') {
          // Replace all existing links with new ones
          updatedLinks = newLinks;
          messageText = `Successfully replaced all footer links with ${newLinks.length} link(s) (all set to nofollow)`;
        } else {
          // insert_and_update_duplicate: Insert new links, update duplicates based on URL
          updatedLinks = [...footerLinks];
          let insertedCount = 0;
          let updatedCount = 0;

          for (const newLink of newLinks) {
            const existingIndex = updatedLinks.findIndex(link => link.url === newLink.url);
            if (existingIndex !== -1) {
              // Update existing link
              updatedLinks[existingIndex] = {
                ...updatedLinks[existingIndex],
                label: newLink.label,
                dofollow: false // Ensure nofollow for imported links
              };
              updatedCount++;
            } else {
              // Insert new link
              updatedLinks.push(newLink);
              insertedCount++;
            }
          }

          messageText = `Successfully imported ${newLinks.length} footer link(s): ${insertedCount} inserted, ${updatedCount} updated (all set to nofollow)`;
        }

        setFooterLinks(updatedLinks);
        saveFooterData(footerText, updatedLinks);
        message.success(messageText);
      } catch (error: any) {
        console.error('Failed to parse CSV:', error);
        message.error(`Failed to parse CSV file: ${error.message}`);
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadFooterLinksSample = () => {
    const sampleData = [
      { text: 'Privacy Policy', link: '/privacy' },
      { text: 'Terms of Service', link: '/terms' },
      { text: 'About Us', link: '/about' },
      { text: 'Contact', link: '/contact' },
    ];

    const csvContent = [
      'text,link',
      ...sampleData.map(row => `"${row.text}","${row.link}"`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'footer-links-sample.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteFooterLink = async (globalIndex: number) => {
    const newFooterLinks = footerLinks.filter((_, index) => index !== globalIndex);
    setFooterLinks(newFooterLinks);
    message.success('Footer link deleted successfully');
    
    // Reset to first page if current page becomes empty
    const newTotalPages = Math.ceil(newFooterLinks.length / footerPageSize);
    if (footerCurrentPage > newTotalPages && newTotalPages > 0) {
      setFooterCurrentPage(newTotalPages);
    }
    
    // Save footer data to config with the new data
    await saveFooterData(footerText, newFooterLinks);
  };

  const handleSaveFooterLink = async (values: any) => {
    try {
      let newFooterLinks: FooterLink[];

      if (editingFooterLinkIndex !== null) {
        const existingLink = footerLinks[editingFooterLinkIndex];
        const linkData: FooterLink = {
          id: existingLink?.id || editingFooterLink?.id || `link_${Date.now()}`,
          label: values.label,
          url: values.url,
          dofollow: values.dofollow || false,
        };

        newFooterLinks = footerLinks.map((link, index) =>
          index === editingFooterLinkIndex ? linkData : { ...link },
        );
        setFooterLinks(newFooterLinks);
        message.success('Footer link updated successfully');
      } else {
        const linkData: FooterLink = {
          id: `link_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          label: values.label,
          url: values.url,
          dofollow: values.dofollow || false,
        };

        newFooterLinks = [...cloneFooterLinks(footerLinks), linkData];
        setFooterLinks(newFooterLinks);
        message.success('Footer link added successfully');
      }

      setFooterModalVisible(false);
      setEditingFooterLink(null);
      setEditingFooterLinkIndex(null);
      footerLinkForm.resetFields();
      
      // Save footer data to config with the new data
      await saveFooterData(footerText, newFooterLinks);
    } catch (error) {
      console.error('Failed to save footer link:', error);
      message.error('Failed to save footer link');
    }
  };

  const handleFooterTextChange = async (newText: string) => {
    setFooterText(newText);
    await saveFooterData(newText, footerLinks);
  };

  // Footer pagination logic
  const getFooterPaginatedLinks = () => {
    const startIndex = (footerCurrentPage - 1) * footerPageSize;
    const endIndex = startIndex + footerPageSize;
    return footerLinks.slice(startIndex, endIndex);
  };

  const getFooterTotalPages = () => {
    return Math.ceil(footerLinks.length / footerPageSize);
  };

  const handleFooterPageChange = (page: number) => {
    setFooterCurrentPage(page);
  };

  // Footer template management functions
  const saveFooterTemplate = async () => {
    if (!footerTemplateName.trim()) {
      message.error('Please enter a template name');
      return;
    }

    const newTemplate = {
      id: Date.now().toString(),
      name: footerTemplateName.trim(),
      text: footerText,
      links: footerLinks,
      createdAt: new Date().toISOString()
    };

    const updatedTemplates = [...footerTemplates, newTemplate];
    setFooterTemplates(updatedTemplates);
    
    // Save to localStorage
    localStorage.setItem('footerTemplates', JSON.stringify(updatedTemplates));
    
    setFooterTemplateName('');
    setFooterTemplateModalVisible(false);
    message.success('Footer template saved successfully!');
  };

  const applyFooterTemplate = async (template: any) => {
    setFooterText(template.text);
    setFooterLinks(template.links);
    setFooterCurrentPage(1);
    
    // Save the applied template to config.json
    await saveFooterData(template.text, template.links);
    
    message.success(`Applied template: ${template.name}`);
  };

  const deleteFooterTemplate = (templateId: string) => {
    const updatedTemplates = footerTemplates.filter(t => t.id !== templateId);
    setFooterTemplates(updatedTemplates);
    localStorage.setItem('footerTemplates', JSON.stringify(updatedTemplates));
    message.success('Template deleted successfully!');
  };

  const loadFooterTemplates = () => {
    const savedTemplates = localStorage.getItem('footerTemplates');
    if (savedTemplates) {
      try {
        setFooterTemplates(JSON.parse(savedTemplates));
      } catch (error) {
        console.error('Error loading footer templates:', error);
      }
    }
  };

  const initSampleFooterLinks = () => {
    const baseId = Date.now();
    const sampleLinks: FooterLink[] = [
      // Navigation Links
      { id: `${baseId}_1`, label: 'Home', url: '/', dofollow: false },
      { id: `${baseId}_2`, label: 'About Us', url: '/about', dofollow: false },
      { id: `${baseId}_3`, label: 'Contact', url: '/contact', dofollow: false },
      { id: `${baseId}_4`, label: 'Services', url: '/services', dofollow: false },
      { id: `${baseId}_5`, label: 'Portfolio', url: '/portfolio', dofollow: false },
      { id: `${baseId}_6`, label: 'Blog', url: '/blog', dofollow: false },
      { id: `${baseId}_7`, label: 'News', url: '/news', dofollow: false },
      { id: `${baseId}_8`, label: 'Gallery', url: '/gallery', dofollow: false },
      { id: `${baseId}_9`, label: 'Team', url: '/team', dofollow: false },
      { id: `${baseId}_10`, label: 'Careers', url: '/careers', dofollow: false },
      { id: `${baseId}_11`, label: 'Support', url: '/support', dofollow: false },
      { id: `${baseId}_12`, label: 'FAQ', url: '/faq', dofollow: false },
      { id: `${baseId}_13`, label: 'Help Center', url: '/help', dofollow: false },
      { id: `${baseId}_14`, label: 'Documentation', url: '/docs', dofollow: false },
      { id: `${baseId}_15`, label: 'Tutorials', url: '/tutorials', dofollow: false },
      { id: `${baseId}_16`, label: 'Community', url: '/community', dofollow: false },
      { id: `${baseId}_17`, label: 'Forum', url: '/forum', dofollow: false },
      { id: `${baseId}_18`, label: 'Live Chat', url: '/chat', dofollow: false },
      { id: `${baseId}_19`, label: 'Privacy Policy', url: '/privacy', dofollow: false },
      { id: `${baseId}_20`, label: 'Terms of Service', url: '/terms', dofollow: false },
      { id: `${baseId}_21`, label: 'Cookie Policy', url: '/cookies', dofollow: false },
      { id: `${baseId}_22`, label: 'Disclaimer', url: '/disclaimer', dofollow: false },
      { id: `${baseId}_23`, label: 'Refund Policy', url: '/refund', dofollow: false },
      { id: `${baseId}_24`, label: 'Shipping Policy', url: '/shipping', dofollow: false },
      { id: `${baseId}_25`, label: 'Sitemap', url: '/sitemap', dofollow: false },
      { id: `${baseId}_26`, label: 'RSS Feed', url: '/rss', dofollow: false },
      { id: `${baseId}_27`, label: 'API Documentation', url: '/api', dofollow: false },
      { id: `${baseId}_28`, label: 'Status Page', url: '/status', dofollow: false },
      { id: `${baseId}_29`, label: 'Newsletter', url: '/newsletter', dofollow: false },
      { id: `${baseId}_30`, label: 'Subscribe', url: '/subscribe', dofollow: false },
      { id: `${baseId}_31`, label: 'Feedback', url: '/feedback', dofollow: false },
      { id: `${baseId}_32`, label: 'Testimonials', url: '/testimonials', dofollow: false },
      { id: `${baseId}_33`, label: 'Reviews', url: '/reviews', dofollow: false },
      { id: `${baseId}_34`, label: 'Partners', url: '/partners', dofollow: false },
      { id: `${baseId}_35`, label: 'Affiliates', url: '/affiliates', dofollow: false },
    ];

    setFooterLinks(sampleLinks);
    setFooterText('© 2024 My Website. All rights reserved.');
    setFooterCurrentPage(1);
    
    // Auto save the sample data
    saveFooterData('© 2024 My Website. All rights reserved.', sampleLinks);
    
    message.success('Sample footer links initialized successfully!');
  };

  const handleAddSection = () => {
    setEditingSection(null);
    setSectionType('video-grid'); // Default to Video Grid
    setBackgroundColor('#ffffff');
    setTextColor('#000000');
    setSelectedVideoIds([]);
    setVideoSelectionMode('random-range'); // Default to Random from range (A -> B)
    setRandomVideoCount(100); // Default to 100 videos
    form.resetFields();
    // Set default values for new section
    form.setFieldsValue({
      type: 'video-grid',
      videoSelectionMode: 'random-range',
      randomVideoCount: 100,
      randomVideoCountMin: 1000,
      randomVideoCountMax: 1500,
      postsPerPage: 40,
      enablePagination: true,
      gridColumns: 5,
      cardStyle: 'elevated',
      animation: 'subtle',
      showVideoInfo: true,
      visible: true,
      padding: '2rem',
      margin: '0'
    });
    setModalVisible(true);
  };

  const handleEditSection = (section: WebsiteSection) => {
    setEditingSection(section);
    setSectionType(section.type);
    setBackgroundColor(section.backgroundColor || '#ffffff');
    setTextColor(section.textColor || '#000000');
    setSelectedVideoIds(section.selectedVideoIds || []);
    setVideoSelectionMode(section.videoSelectionMode || 'manual');
    setRandomVideoCount(section.randomVideoCount || 5);
    setRandomVideoCountMin(section.randomVideoCountMin || 1000);
    setRandomVideoCountMax(section.randomVideoCountMax || 1500);
    
    // Convert imageUrls array to newline-separated string for editing
    const formValues = {
      ...section,
      imageUrls: section.imageUrls ? section.imageUrls.join('\n') : '',
      videoUrls: section.videoUrls ? section.videoUrls.join('\n') : ''
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleSaveSection = async (values: any) => {
    try {
      setLoading(true);
      
      if (editingSection) {
        // Update existing section
        await websiteApi.updateWebsiteSection(id!, editingSection.id, values);
        message.success('Section updated successfully');
      } else {
        // Add new section
        await websiteApi.addWebsiteSection(id!, values);
        message.success('Section added successfully');
      }
      
      setModalVisible(false);
      await loadWebsiteSectionsData();
      await loadWebsiteSections();
      
      // Auto-regenerate website
      try {
        await websiteApi.regenerateWebsite(id!);
        message.success('Website regenerated successfully');
      } catch (error) {
        console.error('Failed to regenerate website:', error);
      }
    } catch (error) {
      message.error('Failed to save section');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = (sectionId: string) => {
    setSectionToDelete(sectionId);
    setDeleteModalVisible(true);
  };

  const confirmDeleteSection = async () => {
    if (!sectionToDelete) return;
    
    try {
      await websiteApi.deleteWebsiteSection(id!, sectionToDelete);
      message.success('Section deleted successfully');
      await loadWebsiteSectionsData();
      await loadWebsiteSections();
      
      // Auto-regenerate website
      try {
        await websiteApi.regenerateWebsite(id!);
        message.success('Website regenerated successfully');
      } catch (error) {
        console.error('Failed to regenerate website:', error);
      }
    } catch (error) {
      message.error('Failed to delete section');
    } finally {
      setDeleteModalVisible(false);
      setSectionToDelete(null);
    }
  };

  const cancelDeleteSection = () => {
    setDeleteModalVisible(false);
    setSectionToDelete(null);
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = sections.findIndex(section => section.id === active.id);
      const newIndex = sections.findIndex(section => section.id === over.id);
      
      const newSections = arrayMove(sections, oldIndex, newIndex);
      setSections(newSections);
      
      // Update order in backend
      try {
        await websiteApi.reorderWebsiteSections(id!, newSections.map(s => s.id));
        message.success('Sections reordered successfully');
        
        // Auto-regenerate website
        try {
          await websiteApi.regenerateWebsite(id!);
          message.success('Website regenerated successfully');
        } catch (error) {
          console.error('Failed to regenerate website:', error);
        }
      } catch (error) {
        message.error('Failed to reorder sections');
        // Revert on error
        await loadWebsiteSectionsData();
        await loadWebsiteSections();
      }
    }
  };

  const handleToggleVisibility = async (sectionId: string, visible: boolean) => {
    try {
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        await websiteApi.updateWebsiteSection(id!, sectionId, { ...section, visible });
        message.success(`Section ${visible ? 'shown' : 'hidden'} successfully`);
        await loadWebsiteSectionsData();
        await loadWebsiteSections();
        
        // Auto-regenerate website
        try {
          await websiteApi.regenerateWebsite(id!);
          message.success('Website regenerated successfully');
        } catch (error) {
          console.error('Failed to regenerate website:', error);
        }
      }
    } catch (error) {
      message.error('Failed to update section visibility');
    }
  };

  // Poll for real progress updates
  const pollProgress = (websiteId: string) => {
    const interval = setInterval(async () => {
      try {
        const progressDataResponse = await progressApi.getProgress(websiteId);
        if (progressDataResponse) {
          setProgressData({
            title: progressDataResponse.title,
            overallProgress: progressDataResponse.overallProgress,
            items: progressDataResponse.items,
            isCompleted: progressDataResponse.isCompleted,
            isError: progressDataResponse.isError,
            successMessage: progressDataResponse.successMessage,
            errorMessage: progressDataResponse.errorMessage,
          });

          // Stop polling when completed or error
          if (progressDataResponse.isCompleted || progressDataResponse.isError) {
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
        clearInterval(interval);
      }
    }, 1000); // Poll every second

    return interval;
  };

  // Simulate NGINX progress updates (no backend polling to avoid conflicts)
  const simulateNginxProgress = (websiteId: string) => {
    const steps = [
      { id: '1', name: 'Creating NGINX config...', status: 'processing' as const },
      { id: '2', name: 'Validating configuration...', status: 'pending' as const },
      { id: '3', name: 'Setting up SSL...', status: 'pending' as const },
      { id: '4', name: 'Reloading NGINX...', status: 'pending' as const },
    ];

    let currentStep = 0;
    const interval = setInterval(() => {
      if (currentStep < steps.length) {
        const updatedSteps = steps.map((step, index) => ({
          ...step,
          status: index <= currentStep ? 'processing' as const : 'pending' as const
        }));
        
        setProgressData(prev => ({
          ...prev,
          items: updatedSteps,
          overallProgress: ((currentStep + 1) / steps.length) * 100,
        }));
        
        currentStep++;
      } else {
        clearInterval(interval);
      }
    }, 2000); // Update every 2 seconds

    return interval;
  };


  const handleGenerateWebsite = async () => {
    try {
      // Check if any section is selected
      const hasSelection = generationSelection.menuStructure || 
                          generationSelection.bannerAds || 
                          generationSelection.footerManagement || 
                          generationSelection.sidebar ||
                          generationSelection.topKeywords ||
                          generationSelection.googleAnalytics ||
                          (generationSelection.websiteSections.enabled && 
                           generationSelection.websiteSections.subSections.some(sub => sub.selected));

      if (!hasSelection) {
        message.warning('Please select at least one section to generate');
        return;
      }

      // Build progress items based on selected sections
      const buildProgressItems = () => {
        const items = [];
        let itemId = 1;

        // Always add the 5 standard steps
        items.push({ id: itemId.toString(), name: 'Preparing files...', status: 'pending' as const });
        itemId++;
        items.push({ id: itemId.toString(), name: 'Generating HTML pages...', status: 'pending' as const });
        itemId++;
        items.push({ id: itemId.toString(), name: 'Creating CSS files...', status: 'pending' as const });
        itemId++;
        items.push({ id: itemId.toString(), name: 'Generating JavaScript...', status: 'pending' as const });
        itemId++;
        items.push({ id: itemId.toString(), name: 'Finalizing...', status: 'pending' as const });
        itemId++;

        // Add selected sections if any
        if (generationSelection.menuStructure) {
          items.push({ id: itemId.toString(), name: 'Generating Menu Structure...', status: 'pending' as const });
          itemId++;
        }

        if (generationSelection.bannerAds) {
          items.push({ id: itemId.toString(), name: 'Generating Banner Ads...', status: 'pending' as const });
          itemId++;
        }

        if (generationSelection.footerManagement) {
          items.push({ id: itemId.toString(), name: 'Generating Footer Management...', status: 'pending' as const });
          itemId++;
        }

        if (generationSelection.sidebar) {
          items.push({ id: itemId.toString(), name: 'Generating Sidebar...', status: 'pending' as const });
          itemId++;
        }

        if (generationSelection.topKeywords) {
          items.push({ id: itemId.toString(), name: 'Generating Top Keywords...', status: 'pending' as const });
          itemId++;
        }

        if (generationSelection.googleAnalytics) {
          items.push({ id: itemId.toString(), name: 'Generating Google Analytics...', status: 'pending' as const });
          itemId++;
        }

        if (generationSelection.websiteSections.enabled) {
          if (generationSelection.websiteSections.subSections.some(sub => sub.selected)) {
            // Generate selected sub-sections
            generationSelection.websiteSections.subSections
              .filter(sub => sub.selected)
              .forEach(sub => {
                items.push({ id: itemId.toString(), name: `Generating ${sub.name}...`, status: 'pending' as const });
                itemId++;
              });
          } else {
            // Generate all sub-sections if none selected
            items.push({ id: itemId.toString(), name: 'Generating All Website Sections...', status: 'pending' as const });
            itemId++;
          }
        }

        return items;
      };

      // Show progress modal with dynamic items
      setProgressData({
        title: 'Generating Selected Sections',
        overallProgress: 0,
        items: buildProgressItems(),
        isCompleted: false,
        isError: false,
        successMessage: '',
        errorMessage: '',
      });
      setProgressVisible(true);
      
      // Start polling for real progress
      pollProgress(id!);
      
      // Generate website with selected sections
      await websiteGeneratorService.generateWebsite(id!, generationSelection);
      
      // Update progress on success
      setProgressData(prev => ({
        ...prev,
        overallProgress: 100,
        items: prev.items.map(item => ({ ...item, status: 'completed' as const })),
        isCompleted: true,
        successMessage: `Website regenerated successfully`,
      }));
    } catch (error: any) {
      console.error('Website Generation Error:', error);
      
      // Show original system error
      let errorMessage = 'Failed to generate website';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.statusText) {
        errorMessage = `${error.response.status}: ${error.response.statusText}`;
      }
      
      setProgressData(prev => ({
        ...prev,
        isError: true,
        errorMessage: errorMessage,
      }));
    }
  };

  const handleGenerateNginx = async () => {
    try {
      // Show progress modal
      setProgressData({
        title: 'Generating NGINX Configuration',
        overallProgress: 0,
        items: [
          { id: '1', name: 'Creating NGINX config...', status: 'pending' },
          { id: '2', name: 'Validating configuration...', status: 'pending' },
          { id: '3', name: 'Setting up SSL...', status: 'pending' },
          { id: '4', name: 'Reloading NGINX...', status: 'pending' },
        ],
        isCompleted: false,
        isError: false,
        successMessage: '',
        errorMessage: '',
      });
      setProgressVisible(true);
      
      // Start simulating NGINX progress
      simulateNginxProgress(id!);
      
      await websiteGeneratorService.generateNginxConfig(id!);
      
      // Update progress on success
      setProgressData(prev => ({
        ...prev,
        overallProgress: 100,
        items: prev.items.map(item => ({ ...item, status: 'completed' as const })),
        isCompleted: true,
        successMessage: `NGINX configuration generated successfully`,
      }));
    } catch (error: any) {
      console.error('NGINX Generation Error:', error);
      
      // Show original system error
      let errorMessage = 'Failed to generate NGINX configuration';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.statusText) {
        errorMessage = `${error.response.status}: ${error.response.statusText}`;
      }
      
      setProgressData(prev => ({
        ...prev,
        isError: true,
        errorMessage: errorMessage,
      }));
    }
  };

  // Generation selection handlers
  const handleMainSectionChange = (section: string, checked: boolean) => {
    setGenerationSelection(prev => ({
      ...prev,
      [section]: checked
    }));
  };

  const handleWebsiteSectionsChange = (checked: boolean) => {
    setGenerationSelection(prev => ({
      ...prev,
      websiteSections: {
        ...prev.websiteSections,
        enabled: checked
      }
    }));
  };

  const handleSubSectionChange = (sectionId: string, checked: boolean) => {
    setGenerationSelection(prev => ({
      ...prev,
      websiteSections: {
        ...prev.websiteSections,
        subSections: prev.websiteSections.subSections.map(sub => 
          sub.id === sectionId ? { ...sub, selected: checked } : sub
        )
      }
    }));
  };

  const handleSelectAllSubSections = (checked: boolean) => {
    setGenerationSelection(prev => ({
      ...prev,
      websiteSections: {
        ...prev.websiteSections,
        subSections: prev.websiteSections.subSections.map(sub => ({
          ...sub,
          selected: checked
        }))
      }
    }));
  };

  const renderVideoGridDetails = (section: WebsiteSection) => {
    if (section.type !== 'video-grid') return null;

    const details = [];
    
    // Total videos
    let totalVideos = 0;
    if (section.videoSelectionMode === 'all') {
      totalVideos = availableVideos.length;
    } else if (section.videoSelectionMode === 'random') {
      totalVideos = section.randomVideoCount || 0;
    } else if (section.videoSelectionMode === 'random-range') {
      // For random range, use the max value for display purposes
      const max = section.randomVideoCountMax || 1500;
      totalVideos = max;
    } else if (section.videoSelectionMode === 'manual') {
      totalVideos = section.selectedVideoIds?.length || 0;
    }
    
    if (totalVideos > 0) {
      details.push(`📹 ${totalVideos} videos`);
    }
    
    // Posts per page
    if (section.postsPerPage) {
      details.push(`📄 ${section.postsPerPage} per page`);
    }
    
    // Random videos count
    if (section.videoSelectionMode === 'random' && section.randomVideoCount) {
      details.push(`🎲 ${section.randomVideoCount} random`);
    }
    
    // Random range
    if (section.videoSelectionMode === 'random-range') {
      const min = section.randomVideoCountMin || 1000;
      const max = section.randomVideoCountMax || 1500;
      details.push(`🎲 Random ${min}-${max}`);
    }
    
    // Grid columns
    if (section.gridColumns) {
      details.push(`📐 ${section.gridColumns} columns`);
    }

    return details.length > 0 ? (
      <div style={{ 
        marginTop: 8, 
        padding: '4px 8px', 
        backgroundColor: 'var(--bg-tertiary)', 
        borderRadius: '4px',
        fontSize: '12px',
        color: 'var(--text-secondary)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        {details.map((detail, index) => (
          <span key={`detail-${section.id}-${index}`} style={{ 
            backgroundColor: 'var(--bg-hover)', 
            padding: '2px 6px', 
            borderRadius: '3px',
            fontSize: '11px',
            color: 'var(--text-primary)'
          }}>
            {detail}
          </span>
        ))}
      </div>
    ) : null;
  };

  const SortableItem: React.FC<{ section: WebsiteSection }> = ({ section }) => {
    const {
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: section.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div ref={setNodeRef} style={style}>
        <Card
          size="small"
          style={{ marginBottom: 8 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DragOutlined />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {sectionTypes.find(t => t.value === section.type)?.icon}
                <span style={{ fontWeight: 'bold' }}>{sectionTypes.find(t => t.value === section.type)?.label}</span>
              </div>
              {section.title && <span>- {section.title}</span>}
              {!section.visible && <span style={{ color: '#999' }}>(Hidden)</span>}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Edit button clicked for section:', section.id);
                  handleEditSection(section);
                }}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Delete button clicked for section:', section.id);
                  handleDeleteSection(section.id);
                }}
              />
              <Button
                type="text"
                icon={section.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Visibility button clicked for section:', section.id);
                  handleToggleVisibility(section.id, !section.visible);
                }}
              />
            </div>
          </div>
          {renderVideoGridDetails(section)}
        </Card>
      </div>
    );
  };

  return (
    <App>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1>
            Website Builder
            {websiteData?.domain && (
              <span style={{ color: '#999', fontWeight: 'normal', fontSize: '0.8em' }}>
                {' '}({websiteData.domain})
              </span>
            )}
          </h1>
          <Space>
            <Button onClick={() => navigate(`/websites/${id}`)}>
              Back to Website
            </Button>
          </Space>
        </div>

        {/* Generation Selection */}
        <Card title="Select Sections to Generate" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={generationSelection.menuStructure}
              onChange={(e) => handleMainSectionChange('menuStructure', e.target.checked)}
            >
              <strong>Menu Structure</strong>
            </Checkbox>
            <div style={{ marginLeft: 20, color: '#666', fontSize: '12px' }}>
              Generate navigation menu and menu items
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={generationSelection.bannerAds}
              onChange={(e) => handleMainSectionChange('bannerAds', e.target.checked)}
            >
              <strong>Banner Ads</strong>
            </Checkbox>
            <div style={{ marginLeft: 20, color: '#666', fontSize: '12px' }}>
              Generate banner advertisements and placements
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={generationSelection.footerManagement}
              onChange={(e) => handleMainSectionChange('footerManagement', e.target.checked)}
            >
              <strong>Footer Management</strong>
            </Checkbox>
            <div style={{ marginLeft: 20, color: '#666', fontSize: '12px' }}>
              Generate footer content, links, and templates
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={generationSelection.sidebar}
              onChange={(e) => handleMainSectionChange('sidebar', e.target.checked)}
            >
              <strong>Sidebar</strong>
            </Checkbox>
            <div style={{ marginLeft: 20, color: '#666', fontSize: '12px' }}>
              Generate sidebar with configured position and width
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={generationSelection.topKeywords}
              onChange={(e) => handleMainSectionChange('topKeywords', e.target.checked)}
            >
              <strong>Top Keywords</strong>
            </Checkbox>
            <div style={{ marginLeft: 20, color: '#666', fontSize: '12px' }}>
              Generate top keywords section in sidebar
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={generationSelection.googleAnalytics}
              onChange={(e) => handleMainSectionChange('googleAnalytics', e.target.checked)}
            >
              <strong>Google Analytics</strong>
            </Checkbox>
            <div style={{ marginLeft: 20, color: '#666', fontSize: '12px' }}>
              Generate Google Analytics script in website header (from saved GA Key)
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={generationSelection.websiteSections.enabled}
              onChange={(e) => handleWebsiteSectionsChange(e.target.checked)}
            >
              <strong>Website Sections</strong>
            </Checkbox>
            <div style={{ marginLeft: 20, color: '#666', fontSize: '12px' }}>
              Generate website content sections
            </div>
            
            {generationSelection.websiteSections.enabled && (
              <div style={{ marginLeft: 20, marginTop: 8 }}>
                <div style={{ marginBottom: 8 }}>
                  <Checkbox
                    checked={generationSelection.websiteSections.subSections.every(sub => sub.selected)}
                    indeterminate={generationSelection.websiteSections.subSections.some(sub => sub.selected) && !generationSelection.websiteSections.subSections.every(sub => sub.selected)}
                    onChange={(e) => handleSelectAllSubSections(e.target.checked)}
                  >
                    Select All Sub-sections
                  </Checkbox>
                </div>
                {generationSelection.websiteSections.subSections.map((subSection) => (
                  <div key={subSection.id} style={{ marginLeft: 20, marginBottom: 4 }}>
                    <Checkbox
                      checked={subSection.selected}
                      onChange={(e) => handleSubSectionChange(subSection.id, e.target.checked)}
                    >
                      {subSection.name}
                    </Checkbox>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        <Card title="Google Analytics" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 12, fontSize: 14, color: 'var(--text-secondary)' }}>
            Nhập Measurement ID (ví dụ: G-XXXXXXXXXX). Chọn Google Analytics trong &quot;Select Sections to Generate&quot; rồi Generate để cập nhật script vào header.
          </div>
          <Space.Compact style={{ width: '100%', maxWidth: 520 }}>
            <Input
              value={gaKey}
              onChange={(e) => setGaKey(e.target.value)}
              placeholder="G-XXXXXXXXXX"
              allowClear
            />
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={gaKeySaving}
              onClick={() => saveGaKeyData()}
            >
              Save GA Key
            </Button>
          </Space.Compact>
        </Card>

        <Card title="Quick Actions" style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space>
              <Button icon={<SaveOutlined />} onClick={handleGenerateWebsite}>
                Generate Website
              </Button>
              {!hasSSL && (
                <Button 
                  icon={<CrownOutlined />} 
                  onClick={handleGenerateNginx}
                  type="primary"
                  loading={sslLoading}
                >
                  Generate NGINX
                </Button>
              )}
              {hasSSL && (
                <Button 
                  icon={<CrownOutlined />} 
                  disabled
                  type="default"
                >
                  SSL Already Configured
                </Button>
              )}
            </Space>
            {websiteData?.domain && (
              <div style={{ 
                padding: '12px',
                backgroundColor: 'var(--bg-tertiary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '13px',
                color: 'var(--text-primary)',
                wordBreak: 'break-all',
                position: 'relative'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <strong>SSL Generation Command:</strong><br />
                    cd /var/www/dirweb && sh generate-ssl.sh {websiteData.domain}
                  </div>
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    size="small"
                    onClick={async () => {
                      const command = `cd /var/www/dirweb && sh generate-ssl.sh ${websiteData.domain}`;
                      try {
                        await navigator.clipboard.writeText(command);
                        message.success('Command copied to clipboard!');
                      } catch (err) {
                        // Fallback for older browsers
                        const textArea = document.createElement('textarea');
                        textArea.value = command;
                        textArea.style.position = 'fixed';
                        textArea.style.opacity = '0';
                        document.body.appendChild(textArea);
                        textArea.select();
                        try {
                          document.execCommand('copy');
                          message.success('Command copied to clipboard!');
                        } catch (fallbackErr) {
                          message.error('Failed to copy command');
                        }
                        document.body.removeChild(textArea);
                      }
                    }}
                    style={{ 
                      flexShrink: 0,
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
              </div>
            )}
          </Space>
        </Card>

        <Card title="Menu Structure" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ 
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '6px',
              fontSize: '14px',
              color: 'var(--text-primary)'
            }}>
              <strong>💡 Tip:</strong> Drag and drop menu items to reorder them. Click "Add Menu Item" to add new items. 
              Menu changes are saved to config.json when you click "Save Menu". Use "Regenerate Website" to apply changes to the live website.
            </div>
          </div>
          
          <div style={{ 
            border: '1px solid var(--border-secondary)', 
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: 'var(--bg-secondary)',
            minHeight: '200px'
          }}>
            <DndKitMenuDragDrop
              menuItems={menuItems}
              onChange={handleMenuItemsChange}
              onShuffle={shuffleMenuItems}
            />
          </div>
          
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button type="primary" onClick={saveMenuItems}>
              Save Menu
            </Button>
          </div>
        </Card>

        <Card title="Banner Ads" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ 
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '6px',
              fontSize: '14px',
              color: 'var(--text-primary)'
            }}>
              <strong>📢 Banner Ads:</strong> Add up to 10 banner advertisements to your website. 
              Choose from Top, Left, Right, or Bottom positions. Each banner needs an image URL and link URL.
            </div>
            
            <div style={{ 
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: 'var(--bg-hover)',
              border: '1px solid var(--border-secondary)',
              borderRadius: '6px',
              fontSize: '13px',
              color: 'var(--text-secondary)'
            }}>
              <strong>💡 Image Size Tips:</strong> For best results, use appropriate image sizes:
              <br />• <strong>Top/Bottom banners:</strong> 728×80, 800×90, 970×90, 1200×90
              <br />• <strong>Left/Right banners:</strong> 300×250, 300×300, 250×300, 160×600
              <br />• <strong>Recommended format:</strong> JPG, PNG, or WebP
              <br />• <strong>File size:</strong> Under 500KB for fast loading
            </div>
          </div>
          
          <div style={{ 
            border: '1px solid var(--border-secondary)', 
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: 'var(--bg-secondary)',
            minHeight: '200px'
          }}>
            {bannerAds.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                No banner ads yet. Click "Add Banner Ad" to get started.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {['top', 'left', 'right', 'bottom'].map((position) => {
                  const positionBanners = bannerAds.filter(banner => banner.position === position);
                  if (positionBanners.length === 0) return null;
                  
                  return (
                    <div key={position}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        marginBottom: '12px',
                        padding: '8px 12px',
                        backgroundColor: 'var(--bg-tertiary)',
                        borderRadius: '6px',
                        border: '1px solid var(--border-secondary)'
                      }}>
                        <span style={{ 
                          padding: '4px 8px', 
                          backgroundColor: 'var(--info)', 
                          color: 'white', 
                          borderRadius: '4px',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          fontWeight: 'bold'
                        }}>
                          {position.toUpperCase()} POSITION
                        </span>
                        <span style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-secondary)',
                          fontWeight: '500'
                        }}>
                          {positionBanners.length} banner{positionBanners.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                        {positionBanners.map((banner) => (
                          <Card key={banner.id} size="small" style={{ marginBottom: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Banner Ad</div>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                  {banner.imageUrl ? 'Image: ✓' : 'Image: ✗'} | 
                                  {banner.linkUrl ? 'Link: ✓' : 'Link: ✗'}
                                </div>
                              </div>
                              <Space>
                                <Button 
                                  size="small" 
                                  icon={<EditOutlined />} 
                                  onClick={() => handleEditBanner(banner)}
                                />
                                <Button 
                                  size="small" 
                                  danger 
                                  icon={<DeleteOutlined />} 
                                  onClick={() => handleDeleteBanner(banner.id)}
                                />
                              </Space>
                            </div>
                            {banner.imageUrl && (
                              <div style={{ marginTop: 8 }}>
                                <img 
                                  src={banner.imageUrl} 
                                  alt="Banner preview" 
                                  style={{ 
                                    width: '100%', 
                                    height: '60px', 
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: '1px solid var(--border-secondary)'
                                  }} 
                                />
                              </div>
                            )}
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAddBanner}
              disabled={bannerAds.length >= 10}
            >
              Add Banner Ad {bannerAds.length >= 10 ? '(Max 10)' : ''}
            </Button>
          </div>
        </Card>

        <Card title="Footer Management" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ 
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '6px',
              fontSize: '14px',
              color: 'var(--text-primary)'
            }}>
              <strong>🔗 Footer Links:</strong> Manage your website footer with custom text and links. 
              Links will be displayed on the right side of the footer.
            </div>
          </div>

          {/* Footer Template Management */}
          <div style={{ 
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: 'var(--bg-tertiary)',
            border: '1px solid var(--border-primary)',
            borderRadius: '6px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <div>
                <strong>📁 Footer Templates:</strong> Save and reuse footer configurations
              </div>
              <Space>
                <Button 
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={initSampleFooterLinks}
                >
                  Init Sample Links
                </Button>
                <Button 
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => setFooterTemplateModalVisible(true)}
                >
                  Save Template
                </Button>
                {footerTemplates.length > 0 && (
                  <Button 
                    size="small"
                    icon={<FolderOpenOutlined />}
                    onClick={() => setFooterTemplateModalVisible(true)}
                  >
                    Load Template
                  </Button>
                )}
              </Space>
            </div>
            
            {footerTemplates.length > 0 && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '8px',
                marginTop: '8px'
              }}>
                {footerTemplates.map((template) => (
                  <div key={template.id} style={{
                    padding: '8px',
                    border: '1px solid var(--border-secondary)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--bg-primary)',
                    fontSize: '12px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {template.name}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      {template.links.length} links • {new Date(template.createdAt).toLocaleDateString()}
                    </div>
                    <Space size="small">
                      <Button 
                        size="small" 
                        type="primary"
                        onClick={() => applyFooterTemplate(template)}
                      >
                        Apply
                      </Button>
                      <Button 
                        size="small" 
                        danger
                        onClick={() => deleteFooterTemplate(template.id)}
                      >
                        Delete
                      </Button>
                    </Space>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div style={{ 
            border: '1px solid var(--border-secondary)', 
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: 'var(--bg-secondary)',
            minHeight: '200px'
          }}>
            {/* Footer Style Selector */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                color: 'var(--text-primary)'
              }}>
                Footer Style
              </label>
              <Select
                value={footerStyle}
                onChange={(value) => {
                  // Save immediately with the new value
                  saveFooterData(undefined, undefined, value, undefined);
                }}
                style={{ width: '100%' }}
                size="large"
                placeholder="Select footer style"
              >
                {footerStyleOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              {footerStyleOptions.find(opt => opt.value === footerStyle) && (
                <div style={{ 
                  marginTop: '8px', 
                  padding: '10px 12px',
                  backgroundColor: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '6px',
                  fontSize: '13px', 
                  color: 'var(--text-secondary)',
                  lineHeight: '1.5'
                }}>
                  <div style={{ fontWeight: 500, marginBottom: '4px', color: 'var(--text-primary)' }}>
                    {footerStyleOptions.find(opt => opt.value === footerStyle)?.label}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {footerStyleOptions.find(opt => opt.value === footerStyle)?.description}
                  </div>
                </div>
              )}
              <div style={{ 
                marginTop: '8px', 
                fontSize: '12px', 
                color: 'var(--text-secondary)',
                fontStyle: 'italic'
              }}>
                Different footer styles help avoid duplicate content for better SEO
              </div>
            </div>

            {/* Company Description */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                color: 'var(--text-primary)'
              }}>
                Company Description (Optional)
              </label>
              <TextArea
                value={footerCompanyDescription}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFooterCompanyDescription(newValue);
                  
                  // Clear previous timeout
                  if (companyDescriptionDebounceRef.current) {
                    clearTimeout(companyDescriptionDebounceRef.current);
                  }
                  
                  // Set new timeout for debounce (500ms delay)
                  companyDescriptionDebounceRef.current = setTimeout(() => {
                    saveFooterData(undefined, undefined, undefined, newValue);
                  }, 500);
                }}
                placeholder="Enter a brief description about your company..."
                rows={3}
                style={{ marginBottom: '8px' }}
              />
            </div>

            {/* Footer Text */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                color: 'var(--text-primary)'
              }}>
                Footer Text (Copyright)
              </label>
              <Input
                value={footerText}
                onChange={(e) => handleFooterTextChange(e.target.value)}
                placeholder="© 2024 My Website. All rights reserved."
                style={{ marginBottom: '8px' }}
              />
            </div>

            {/* Footer Links */}
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <label style={{ 
                  fontWeight: 'bold',
                  color: 'var(--text-primary)'
                }}>
                  Footer Links
                </label>
                <Space>
                  <Button 
                    size="small"
                    icon={<UploadOutlined />}
                    onClick={() => setIsImportFooterLinksModalVisible(true)}
                  >
                    Upload CSV
                  </Button>
                  <Button 
                    type="primary" 
                    size="small"
                    icon={<PlusOutlined />} 
                    onClick={handleAddFooterLink}
                  >
                    Add Link
                  </Button>
                </Space>
              </div>

              {footerLinks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                  No footer links yet. Click "Add Link" to get started.
                </div>
              ) : (
                <>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: '12px' 
                  }}>
                    {getFooterPaginatedLinks().map((link, pageIndex) => {
                      const globalIndex = (footerCurrentPage - 1) * footerPageSize + pageIndex;
                      return (
                      <Card key={link.id} size="small" style={{ marginBottom: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{link.label}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                              {link.url}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 2 }}>
                              {link.dofollow ? '🔗 Dofollow' : '🚫 Nofollow'}
                            </div>
                          </div>
                          <Space>
                            <Button 
                              size="small" 
                              icon={<EditOutlined />} 
                              onClick={() => handleEditFooterLink(link, pageIndex)}
                            />
                            <Button 
                              size="small" 
                              danger 
                              icon={<DeleteOutlined />} 
                              onClick={() => handleDeleteFooterLink(globalIndex)}
                            />
                          </Space>
                        </div>
                      </Card>
                    );
                    })}
                  </div>
                  
                  {/* Pagination Controls */}
                  {footerLinks.length > footerPageSize && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginTop: '16px',
                      padding: '12px',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderRadius: '6px',
                      border: '1px solid var(--border-secondary)'
                    }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Showing {((footerCurrentPage - 1) * footerPageSize) + 1} to {Math.min(footerCurrentPage * footerPageSize, footerLinks.length)} of {footerLinks.length} links
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Button 
                          size="small"
                          disabled={footerCurrentPage === 1}
                          onClick={() => handleFooterPageChange(footerCurrentPage - 1)}
                        >
                          ← Previous
                        </Button>
                        <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
                          Page {footerCurrentPage} of {getFooterTotalPages()}
                        </span>
                        <Button 
                          size="small"
                          disabled={footerCurrentPage === getFooterTotalPages()}
                          onClick={() => handleFooterPageChange(footerCurrentPage + 1)}
                        >
                          Next →
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Card>

        <SidebarBuilder
          config={sidebarConfig}
          availableVideos={availableVideos}
          onConfigChange={(newConfig) => {
            setSidebarConfig(newConfig);
            saveSidebarData(newConfig);
          }}
        />

        <TopKeywordsBuilder
          config={topKeywordsConfig}
          onConfigChange={(newConfig) => {
            setTopKeywordsConfig(newConfig);
            saveTopKeywordsData(newConfig);
          }}
        />

        <Card title="Website Sections">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {sections.map(section => (
                <SortableItem key={section.id} section={section} />
              ))}
            </SortableContext>
          </DndContext>
          
          {sections.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              No sections yet. Click "Add Section" to get started.
            </div>
          )}
          
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddSection}>
              Add Section
            </Button>
          </div>
        </Card>

        <Modal
          title={editingSection ? 'Edit Section' : 'Add New Section'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={700}
          style={{ top: 20 }}
          styles={{
            body: {
              height: '70vh',
              overflowY: 'auto'
            }
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSaveSection}
            initialValues={{
              type: 'video-grid', // Default to Video Grid
              padding: '2rem',
              margin: '0',
              visible: true,
              videoSelectionMode: 'random-range', // Default to Random from range (A -> B)
              randomVideoCount: 100, // Default to 100 videos
              randomVideoCountMin: 1000, // Default min for random range
              randomVideoCountMax: 1500, // Default max for random range
              postsPerPage: 40, // Default to 40 posts per page
              enablePagination: true,
              gridColumns: 5,
              cardStyle: 'elevated',
              animation: 'subtle',
              showVideoInfo: true
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}
          >
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
              <Form.Item name="type" label="Section Type" rules={[{ required: true }]}>
                <Select onChange={setSectionType}>
                  {sectionTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {type.icon}
                        {type.label}
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="title" label="Title">
                <Input placeholder="Section title" />
              </Form.Item>

              <Form.Item name="subtitle" label="Subtitle">
                <Input placeholder="Section subtitle" />
              </Form.Item>

              {/* Content field - Rich Text Editor for text, hero, content sections */}
              {(sectionType === 'text' || sectionType === 'hero' || sectionType === 'content') && (
                <Form.Item 
                  name="richContent" 
                  label={sectionType === 'hero' ? 'Hero Content' : sectionType === 'text' ? 'Text Content' : 'Content'}
                >
                  <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.richContent !== currentValues.richContent}>
                    {() => (
                      <RichTextEditor
                        value={form.getFieldValue('richContent') || ''}
                        onChange={(value: string) => form.setFieldValue('richContent', value)}
                        placeholder="Enter your content here..."
                      />
                    )}
                  </Form.Item>
                </Form.Item>
              )}

              {/* Image URL field - Hidden for gallery and video-grid sections */}
              {sectionType !== 'gallery' && sectionType !== 'video-grid' && (
                <Form.Item name="imageUrl" label="Image URL">
                  <Input placeholder="https://example.com/image.jpg" />
                </Form.Item>
              )}

              {/* Gallery Images field - Only for gallery sections */}
              {sectionType === 'gallery' && (
                <Form.Item name="imageUrls" label="Gallery Images (one URL per line)">
                  <TextArea 
                    rows={4} 
                    placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg&#10;https://example.com/image3.jpg"
                  />
                </Form.Item>
              )}

              {/* Video URL field - Hidden for video-grid sections */}
              {sectionType !== 'video-grid' && (
                <Form.Item name="videoUrl" label="Video URL">
                  <Input placeholder="https://youtube.com/watch?v=..." />
                </Form.Item>
              )}

              {/* Video Grid specific fields */}
              {sectionType === 'video-grid' && (
                <>
                  <Form.Item name="videoSelectionMode" label="Video Selection Mode">
                    <Select onChange={setVideoSelectionMode}>
                      <Option value="manual">Manual selection</Option>
                      <Option value="all">Add all videos to this video grid</Option>
                      <Option value="random">Pick random videos</Option>
                      <Option value="random-range">Random from range (A → B)</Option>
                    </Select>
                  </Form.Item>

                  {videoSelectionMode === 'manual' ? (
                    <Form.Item name="selectedVideoIds" label="Select Videos">
                      <Select
                        mode="multiple"
                        showSearch
                        placeholder="Select videos"
                        optionFilterProp="children"
                        value={selectedVideoIds}
                        onChange={setSelectedVideoIds}
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {availableVideos.map(video => (
                          <Option key={video.id} value={video.id}>
                            {video.title}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : videoSelectionMode === 'random' ? (
                    <Form.Item name="randomVideoCount" label="Number of Random Videos">
                      <InputNumber
                        min={1}
                        max={10000}
                        value={randomVideoCount}
                        onChange={(value) => setRandomVideoCount(value || 5)}
                        style={{ width: '100%' }}
                        placeholder="Enter number of videos to pick randomly"
                      />
                    </Form.Item>
                  ) : videoSelectionMode === 'random-range' ? (
                    <>
                      <Form.Item name="randomVideoCountMin" label="Minimum (A)">
                        <InputNumber
                          min={1}
                          max={100000}
                          value={randomVideoCountMin}
                          onChange={(value) => setRandomVideoCountMin(value || 1000)}
                          style={{ width: '100%' }}
                          placeholder="Enter minimum number (e.g., 1000)"
                        />
                      </Form.Item>
                      <Form.Item name="randomVideoCountMax" label="Maximum (B)">
                        <InputNumber
                          min={1}
                          max={100000}
                          value={randomVideoCountMax}
                          onChange={(value) => setRandomVideoCountMax(value || 1500)}
                          style={{ width: '100%' }}
                          placeholder="Enter maximum number (e.g., 1500)"
                        />
                      </Form.Item>
                      <div style={{ 
                        padding: '8px 12px', 
                        backgroundColor: '#e6f7ff', 
                        border: '1px solid #91d5ff',
                        borderRadius: '4px',
                        marginBottom: '16px',
                        fontSize: '12px',
                        color: '#0050b3'
                      }}>
                        ℹ️ System will pick a random number X between {randomVideoCountMin} and {randomVideoCountMax}, then select X random videos.
                      </div>
                    </>
                  ) : (
                    <div style={{ 
                      padding: '12px 16px', 
                      backgroundColor: '#f6ffed', 
                      border: '1px solid #b7eb8f', 
                      borderRadius: '6px',
                      color: '#52c41a',
                      fontSize: '14px',
                      marginBottom: '20px',
                    }}>
                      📹 All videos will be attached to this section
                    </div>
                  )}

                  <Form.Item name="postsPerPage" label="Posts Per Page">
                    <Input type="number" min={1} max={50} placeholder="Number of videos per page" />
                  </Form.Item>

                  <Form.Item name="enablePagination" valuePropName="checked">
                    <Checkbox>Enable Pagination</Checkbox>
                  </Form.Item>

                  <Form.Item name="gridColumns" label="Grid Columns">
                    <Select>
                      <Option value={2}>2 Columns</Option>
                      <Option value={3}>3 Columns</Option>
                      <Option value={4}>4 Columns</Option>
                      <Option value={5}>5 Columns</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="cardStyle" label="Card Style">
                    <Select>
                      <Option value="glass">Glass</Option>
                      <Option value="elevated">Elevated</Option>
                      <Option value="outlined">Outlined</Option>
                      <Option value="flat">Flat</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="animation" label="Animation">
                    <Select>
                      <Option value="dynamic">Dynamic</Option>
                      <Option value="subtle">Subtle</Option>
                      <Option value="none">None</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="showVideoInfo" valuePropName="checked">
                    <Checkbox>Show Video Info (titles, durations, etc.)</Checkbox>
                  </Form.Item>
                </>
              )}

              {/* Background Color field - Hidden for video-grid sections */}
              {sectionType !== 'video-grid' && (
                <Form.Item name="backgroundColor" label="Background Color">
                  <Input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => {
                      setBackgroundColor(e.target.value);
                      form.setFieldValue('backgroundColor', e.target.value);
                    }}
                    style={{ width: 60, height: 32 }}
                  />
                </Form.Item>
              )}

              {/* Text Color field - Hidden for rich text sections */}
              {sectionType !== 'text' && sectionType !== 'hero' && sectionType !== 'content' && (
                <Form.Item name="textColor" label="Text Color">
                  <Input
                    type="color"
                    value={textColor}
                    onChange={(e) => {
                      setTextColor(e.target.value);
                      form.setFieldValue('textColor', e.target.value);
                    }}
                    style={{ width: 60, height: 32 }}
                  />
                </Form.Item>
              )}


              <Form.Item name="padding" label="Padding">
                <Input placeholder="2rem" />
              </Form.Item>

              <Form.Item name="margin" label="Margin">
                <Input placeholder="0" />
              </Form.Item>

              <Form.Item name="fullWidth" valuePropName="checked">
                <Checkbox>Full width (100%)</Checkbox>
              </Form.Item>

              <Form.Item name="isVisible" valuePropName="checked">
                <Checkbox>Visible</Checkbox>
              </Form.Item>
            </div>

            {/* Fixed Buttons at Bottom */}
            <div style={{
              borderTop: '1px solid #f0f0f0',
              paddingTop: '16px',
              marginTop: '16px',
              backgroundColor: '#fff',
              position: 'sticky',
              bottom: 0,
              zIndex: 10
            }}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setModalVisible(false)}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {editingSection ? 'Update Section' : 'Add Section'}
                  </Button>
                </Space>
              </Form.Item>
            </div>
          </Form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          title="Delete Section"
          open={deleteModalVisible}
          onCancel={cancelDeleteSection}
          footer={[
            <Button key="cancel" onClick={cancelDeleteSection}>
              Cancel
            </Button>,
            <Button 
              key="delete" 
              type="primary" 
              danger 
              onClick={confirmDeleteSection}
              loading={loading}
            >
              Delete
            </Button>
          ]}
          width={400}
          centered
        >
          <div style={{ padding: '20px 0' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '16px',
              fontSize: '16px',
              color: '#666'
            }}>
              <span style={{ 
                fontSize: '24px', 
                marginRight: '12px',
                color: '#ff4d4f'
              }}>
                ⚠️
              </span>
              Are you sure you want to delete this section?
            </div>
            <p style={{ 
              margin: 0, 
              color: '#999',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              This action cannot be undone. The section will be permanently removed from your website.
            </p>
          </div>
        </Modal>

        {/* Banner Ad Modal */}
        <Modal
          title={editingBanner ? 'Edit Banner Ad' : 'Add Banner Ad'}
          open={bannerModalVisible}
          onCancel={() => {
            setBannerModalVisible(false);
            setEditingBanner(null);
          }}
          footer={null}
          width={600}
        >
          <Form
            layout="vertical"
            onFinish={handleSaveBanner}
            initialValues={editingBanner ? {
              position: editingBanner.position,
              imageUrl: editingBanner.imageUrl,
              linkUrl: editingBanner.linkUrl
            } : {
              position: 'top'
            }}
          >
            <Form.Item
              name="position"
              label="Position"
              rules={[{ required: true, message: 'Please select a position' }]}
            >
              <Select placeholder="Select banner position">
                <Option value="top">Top</Option>
                <Option value="left">Left</Option>
                <Option value="right">Right</Option>
                <Option value="bottom">Bottom</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="imageUrl"
              label="Image URL"
              rules={[
                { required: true, message: 'Please enter image URL' },
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
            >
              <Input placeholder="https://example.com/banner-image.jpg" />
            </Form.Item>
            
            <div style={{ 
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: 'var(--bg-hover)',
              border: '1px solid var(--border-secondary)',
              borderRadius: '6px',
              fontSize: '13px',
              color: 'var(--text-secondary)'
            }}>
              <strong>💡 Image Size Recommendations:</strong>
              <br />• <strong>Top/Bottom:</strong> 728×80, 800×90, 970×90, 1200×90
              <br />• <strong>Left/Right:</strong> 300×250, 300×300, 250×300, 160×600
              <br />• <strong>Format:</strong> JPG, PNG, or WebP
              <br />• <strong>Size:</strong> Under 500KB for fast loading
            </div>

            <Form.Item
              name="linkUrl"
              label="Link URL"
              rules={[
                { required: true, message: 'Please enter link URL' },
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
            >
              <Input placeholder="https://example.com/landing-page" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setBannerModalVisible(false);
                  setEditingBanner(null);
                }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingBanner ? 'Update Banner' : 'Add Banner'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Footer Link Modal */}
        <Modal
          title={editingFooterLink ? 'Edit Footer Link' : 'Add Footer Link'}
          open={footerModalVisible}
          onCancel={() => {
            setFooterModalVisible(false);
            setEditingFooterLink(null);
            setEditingFooterLinkIndex(null);
          }}
          footer={null}
          width={500}
        >
          <Form
            form={footerLinkForm}
            layout="vertical"
            onFinish={handleSaveFooterLink}
          >
            <Form.Item
              name="label"
              label="Link Text"
              rules={[{ required: true, message: 'Please enter link text' }]}
            >
              <Input placeholder="e.g., Home, About, Contact" />
            </Form.Item>

            <Form.Item
              name="url"
              label="Link URL"
              rules={[
                { required: true, message: 'Please enter link URL' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    
                    // Check if it's a valid URL (with protocol)
                    const urlPattern = /^https?:\/\/.+/;
                    if (urlPattern.test(value)) {
                      return Promise.resolve();
                    }
                    
                    // Check if it's a valid URI path (starts with /)
                    const uriPattern = /^\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]*$/;
                    if (uriPattern.test(value)) {
                      return Promise.resolve();
                    }
                    
                    // Check if it's a domain without protocol
                    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
                    if (domainPattern.test(value)) {
                      return Promise.resolve();
                    }
                    
                    return Promise.reject(new Error('Please enter a valid URL (e.g., https://example.com), URI path (e.g., /about), or domain (e.g., example.com)'));
                  }
                }
              ]}
            >
              <Input placeholder="https://example.com, /about, or example.com" />
            </Form.Item>

            <Form.Item
              name="dofollow"
              valuePropName="checked"
              style={{ marginBottom: 16 }}
            >
              <Checkbox>
                🔗 Dofollow Link (Allow search engines to follow this link)
              </Checkbox>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setFooterModalVisible(false);
                  setEditingFooterLink(null);
                  setEditingFooterLinkIndex(null);
                  footerLinkForm.resetFields();
                }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingFooterLink ? 'Update Link' : 'Add Link'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Footer Template Modal */}
        <Modal
          title="Footer Template Management"
          open={footerTemplateModalVisible}
          onCancel={() => {
            setFooterTemplateModalVisible(false);
            setFooterTemplateName('');
          }}
          footer={null}
          width={600}
        >
          <div style={{ marginBottom: '16px' }}>
            <h4>Save Current Footer as Template</h4>
            <Input
              placeholder="Enter template name"
              value={footerTemplateName}
              onChange={(e) => setFooterTemplateName(e.target.value)}
              style={{ marginBottom: '12px' }}
            />
            <Button 
              type="primary" 
              onClick={saveFooterTemplate}
              disabled={!footerTemplateName.trim()}
            >
              Save Template
            </Button>
          </div>

          {footerTemplates.length > 0 && (
            <div>
              <h4>Available Templates</h4>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                border: '1px solid var(--border-secondary)',
                borderRadius: '4px',
                padding: '8px'
              }}>
                {footerTemplates.map((template) => (
                  <div key={template.id} style={{
                    padding: '12px',
                    border: '1px solid var(--border-secondary)',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    backgroundColor: 'var(--bg-secondary)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{template.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {template.links.length} links • {new Date(template.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <Space>
                        <Button 
                          type="primary"
                          size="small"
                          onClick={() => {
                            applyFooterTemplate(template);
                            setFooterTemplateModalVisible(false);
                          }}
                        >
                          Apply
                        </Button>
                        <Button 
                          danger
                          size="small"
                          onClick={() => deleteFooterTemplate(template.id)}
                        >
                          Delete
                        </Button>
                      </Space>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      <strong>Text:</strong> {template.text || 'No text'}
                    </div>
                    {template.links.length > 0 && (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                        <strong>Links:</strong> {template.links.map(link => link.label).join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal>

        {/* Progress Modal */}
        <ProgressModal
          visible={progressVisible}
          onClose={() => setProgressVisible(false)}
          title={progressData.title}
          overallProgress={progressData.overallProgress}
          items={progressData.items}
          isCompleted={progressData.isCompleted}
          isError={progressData.isError}
          successMessage={progressData.successMessage}
          errorMessage={progressData.errorMessage}
        />

        {/* Import Footer Links Modal */}
        <Modal
          title="Import Footer Links"
          open={isImportFooterLinksModalVisible}
          onCancel={() => setIsImportFooterLinksModalVisible(false)}
          footer={null}
          width={600}
        >
          <div style={{ marginBottom: 16 }}>
            <p>Upload a CSV file to import multiple footer links at once.</p>
            <p><strong>Required columns:</strong> text, link</p>
            
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                Import Mode:
              </label>
              <Radio.Group
                value={importFooterLinksMode}
                onChange={(e) => setImportFooterLinksMode(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical">
                  <Radio value="insert_and_update_duplicate">
                    <div>
                      <strong>Insert and Update Duplicate</strong>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        Insert new links into the list. If a link with the same URL already exists, update it.
                      </div>
                    </div>
                  </Radio>
                  <Radio value="replace_all">
                    <div>
                      <strong>Replace All</strong>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        Remove all existing footer links and replace with links from CSV.
                      </div>
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </div>

            <div style={{ marginTop: 12, padding: 12, backgroundColor: '#f0f0f0', borderRadius: 4, fontSize: '12px' }}>
              <p style={{ margin: 0, marginBottom: 8 }}><strong>Note:</strong></p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>All imported links will be set to <strong>nofollow</strong> (dofollow: false)</li>
                <li>CSV format: <code>text,link</code> (or <code>label,url</code>)</li>
                <li>Example: <code>"Privacy Policy","/privacy"</code></li>
                <li>Duplicate detection is based on <strong>URL</strong> (case-sensitive)</li>
              </ul>
            </div>
          </div>
          
          <Upload.Dragger
            accept=".csv"
            beforeUpload={(file) => {
              handleUploadFooterLinks(file);
              return false; // Prevent auto upload
            }}
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag CSV file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for CSV files only
            </p>
          </Upload.Dragger>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button 
              type="link" 
              onClick={handleDownloadFooterLinksSample}
              style={{ padding: 0 }}
            >
              📥 Download sample CSV file
            </Button>
          </div>
        </Modal>

      </div>
    </App>
  );
};

export default WebsiteBuilder;